import { useEffect, useState } from "react";
import { TFunction } from "i18next";
import {
  Users,
  LayoutGrid,
  MapIcon as Map,
  Crosshair,
} from "../../../../components/icons";
import BasketballCourt from "../../../../components/BasketballCourt";
import TacticalField from "./TacticalField";
import type {
  TacticalPosition,
  Formation,
} from "../../hooks/useTacticalPositions";
import { FieldCoordinate, Match, Player } from "../../types";

type PositionGroup = "guard" | "forward" | "center" | "other";

const POSITION_GROUP_ORDER: PositionGroup[] = [
  "guard",
  "forward",
  "center",
  "other",
];

const normalizePosition = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .trim();

const getPositionGroup = (raw?: string | null): PositionGroup => {
  const normalized = normalizePosition(raw || "");
  if (!normalized) return "other";
  if (
    normalized.includes("pg") ||
    normalized.includes("sg") ||
    normalized.includes("guard") ||
    normalized.includes("pointguard") ||
    normalized.includes("shootingguard")
  ) {
    return "guard";
  }
  if (
    normalized.includes("sf") ||
    normalized.includes("pf") ||
    normalized.includes("forward") ||
    normalized.includes("smallforward") ||
    normalized.includes("powerforward")
  ) {
    return "forward";
  }
  if (
    normalized.includes("c") ||
    normalized.includes("center")
  ) {
    return "center";
  }
  return "other";
};

const getGroupMeta = (group: PositionGroup) => {
  switch (group) {
    case "guard":
      return {
        label: "G",
        dot: "bg-blue-400",
        border: "border-blue-500/60",
        badge: "bg-blue-500/10 text-blue-200 border-blue-500/40",
      };
    case "forward":
      return {
        label: "F",
        dot: "bg-emerald-400",
        border: "border-emerald-500/60",
        badge: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
      };
    case "center":
      return {
        label: "C",
        dot: "bg-purple-400",
        border: "border-purple-500/60",
        badge: "bg-purple-500/10 text-purple-200 border-purple-500/40",
      };
    default:
      return {
        label: "OTH",
        dot: "bg-slate-400",
        border: "border-slate-500/50",
        badge: "bg-slate-500/10 text-slate-200 border-slate-500/40",
      };
  }
};

const sortPlayersByGroup = (players: Player[]) =>
  [...players].sort((a, b) => {
    const groupA = getPositionGroup(a.position);
    const groupB = getPositionGroup(b.position);
    const groupDelta =
      POSITION_GROUP_ORDER.indexOf(groupA) -
      POSITION_GROUP_ORDER.indexOf(groupB);
    if (groupDelta !== 0) return groupDelta;
    return a.jersey_number - b.jersey_number;
  });

interface PlayerSelectorPanelProps {
  match: Match;
  flipSides?: boolean;
  selectedPlayer: Player | null;
  selectedTeam: "home" | "away" | "both";
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { foulCount: number; ejected: boolean; disqualified: boolean }
  >;
  onFieldIds: { home: Set<string>; away: Set<string> };
  onPlayerClick: (player: Player) => void;
  onFieldPlayerClick?: (
    player: Player,
    anchor: { xPercent: number; yPercent: number },
    location: [number, number],
    side: "home" | "away",
  ) => void;
  onFieldDestinationClick?: (coordinate: FieldCoordinate) => void;
  fieldOverlay?: React.ReactNode;
  forceFieldMode?: boolean;
  forceListMode?: boolean;
  forceTacticalMode?: boolean;
  priorityPlayerId?: string | null;
  isReadOnly?: boolean;
  dragLocked?: boolean;
  showDestinationControls?: boolean;

  /** Tactical field support */
  getDisplayPosition?: (
    playerId: string,
    flipSides: boolean,
  ) => TacticalPosition;
  onPlayerDragEnd?: (
    playerId: string,
    displayX: number,
    displayY: number,
    playerPosition: string | undefined,
    side: "home" | "away",
  ) => void;
  draggingPlayerId?: string | null;
  onTacticalDragStart?: (playerId: string) => void;
  onTacticalDragStop?: () => void;
  allTacticalPositions?: Map<string, TacticalPosition>;
  homeFormation?: Formation | null;
  awayFormation?: Formation | null;
  applyFormation?: (side: "home" | "away", formation: Formation | null) => void;
  /** When set, only these player nodes are visible on the field */
  visiblePlayerIds?: Set<string>;
  t: TFunction<"logger">;
}

const PlayerSelectorPanel = ({
  match,
  flipSides = false,
  selectedPlayer,
  selectedTeam,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  onFieldIds,
  onPlayerClick,
  onFieldPlayerClick,
  onFieldDestinationClick,
  fieldOverlay,
  forceFieldMode = false,
  forceListMode = false,
  forceTacticalMode = false,
  priorityPlayerId = null,
  isReadOnly = false,
  dragLocked = true,
  showDestinationControls = false,

  getDisplayPosition,
  onPlayerDragEnd,
  draggingPlayerId,
  onTacticalDragStart,
  onTacticalDragStop,
  allTacticalPositions,
  homeFormation: _homeFormation,
  awayFormation: _awayFormation,
  applyFormation: _applyFormation,
  visiblePlayerIds,
  t,
}: PlayerSelectorPanelProps) => {
  const [viewMode, setViewMode] = useState<"list" | "field" | "tactical">(
    forceTacticalMode ? "tactical" : forceFieldMode ? "field" : "list",
  );
  const resolvedViewMode = forceListMode
    ? "list"
    : forceTacticalMode
      ? "tactical"
      : forceFieldMode
        ? "field"
        : viewMode;

  useEffect(() => {
    if (forceListMode) {
      setViewMode("list");
      return;
    }
    if (forceTacticalMode) {
      setViewMode("tactical");
      return;
    }
    if (!forceFieldMode) return;
    setViewMode("field");
  }, [forceFieldMode, forceListMode, forceTacticalMode]);
  const prioritizePlayers = (players: Player[]) => {
    if (!priorityPlayerId) return players;
    const index = players.findIndex((p) => p.id === priorityPlayerId);
    if (index <= 0) return players;
    const prioritized = [
      players[index],
      ...players.slice(0, index),
      ...players.slice(index + 1),
    ];
    return prioritized;
  };

  // Filter teams based on selected team
  const teamsToShow =
    selectedTeam === "home"
      ? [
          {
            labelColor: "bg-red-600" as const,
            team: match.home_team,
            tone: "home" as const,
          },
        ]
      : selectedTeam === "away"
        ? [
            {
              labelColor: "bg-blue-600" as const,
              team: match.away_team,
              tone: "away" as const,
            },
          ]
        : [
            {
              labelColor: "bg-red-600" as const,
              team: match.home_team,
              tone: "home" as const,
            },
            {
              labelColor: "bg-blue-600" as const,
              team: match.away_team,
              tone: "away" as const,
            },
          ];

  const renderPlayerGrid = (
    players: Player[],
    tone: "home" | "away",
    disabled = false,
  ) => (
    <div
      className="grid gap-2 grid-cols-1"
    >
      {sortPlayersByGroup(players).map((player) => {
        const group = getPositionGroup(player.position);
        const meta = getGroupMeta(group);
        const isSelected = selectedPlayer?.id === player.id;
        const foulStatus = disciplinaryStatusByPlayer?.[player.id];
        const hasFouls = (foulStatus?.foulCount || 0) > 0;
        const isExpelled = expelledPlayerIds?.has(player.id) ?? false;
        const isDisabled = disabled || isExpelled;
        return (
          <button
            key={player.id}
            data-testid={`player-card-${player.id}`}
            data-player-row="true"
            data-position-group={group}
            onClick={() => !isDisabled && onPlayerClick(player)}
            disabled={isDisabled}
            className={`w-full max-w-[420px] mx-auto px-3 py-2 rounded-lg border-l-4 text-left transition-all ${
              isDisabled
                ? "bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed"
                : isSelected
                  ? "bg-blue-900/40 border-blue-500 ring-1 ring-blue-500"
                  : tone === "home"
                    ? "bg-red-900/20 border-red-900/50 hover:border-red-500/50 hover:bg-red-900/30"
                    : "bg-blue-900/20 border-blue-900/50 hover:border-blue-500/50 hover:bg-blue-900/30"
            } ${meta.border}`}
          >
            <div className="flex items-center gap-2.5">
              <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
              <span
                data-testid={`player-number-${player.id}`}
                className={`text-xs font-bold min-w-[2.25rem] ${
                  isDisabled ? "text-slate-600" : "text-slate-200"
                }`}
              >
                #{player.jersey_number}
              </span>
              <span
                data-testid={`player-name-${player.id}`}
                className={`flex-1 text-xs sm:text-sm truncate ${
                  isDisabled ? "text-slate-600" : "text-slate-100"
                }`}
              >
                {player.full_name}
              </span>
              <span
                data-testid={`player-position-${player.id}`}
                className={`text-[9px] uppercase tracking-wider border px-1.5 py-0.5 rounded-full ${meta.badge}`}
              >
                {player.position || meta.label}
              </span>
              {hasFouls && (
                <span
                  data-testid={`player-foul-count-${player.id}`}
                  className="inline-flex items-center rounded-md border border-orange-400/50 bg-orange-500/20 px-1.5 py-0.5 text-[10px] font-bold text-orange-300"
                >
                  {foulStatus!.foulCount}F
                </span>
              )}
              {isExpelled && (
                <span
                  data-testid={`player-disabled-badge-${player.id}`}
                  className="inline-flex items-center rounded-md border border-slate-500/70 bg-slate-700/70 px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
                >
                  Disabled
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const selectionLocked = isReadOnly;
  // When clock is stopped (isReadOnly), always allow node repositioning
  const effectiveDragLocked = isReadOnly ? false : dragLocked;
  const fullOverlayBlocked = selectionLocked && effectiveDragLocked;
  const resolvedFieldOverlay = fieldOverlay;

  return (
    <div
      className="bg-slate-800 rounded-lg shadow p-6 border border-slate-700 relative overflow-visible"
      data-testid="player-grid"
    >
      {fullOverlayBlocked && (
        <div className="absolute inset-0 z-10 bg-slate-900/70 backdrop-blur-sm border border-amber-500/40">
          <div className="p-4 flex items-start gap-3 text-amber-200">
            <div className="mt-0.5 h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <div>
              <p className="font-semibold text-amber-100">
                {t("playerSelectLocked", "Player selection is paused")}
              </p>
              <p className="text-sm text-amber-200/80">
                {t(
                  "playerSelectLockedHint",
                  "Start the clock to enable selecting players.",
                )}
              </p>
            </div>
          </div>
          {resolvedFieldOverlay && (
            <div className="absolute inset-0">
              <div className="relative h-full w-full">
                {resolvedFieldOverlay}
              </div>
            </div>
          )}
        </div>
      )}
      {selectionLocked && !effectiveDragLocked && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-900/40 border border-amber-500/30 flex items-center gap-2 text-amber-200 text-sm">
          <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="font-medium">
            {t("dragOnlyMode", "Drag-only mode")}
          </span>
          <span className="text-amber-200/70">
            —{" "}
            {t(
              "dragOnlyHint",
              "You can reposition players. Start the clock to log actions.",
            )}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
          <Users size={20} />
          {t("selectPlayer", "Select Player")}
        </h2>
        {!forceFieldMode && !forceListMode && !forceTacticalMode && (
          <div className="flex bg-slate-900 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md ${
                viewMode === "list"
                  ? "bg-slate-700 shadow text-blue-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
              title="List View"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode("field")}
              className={`p-1.5 rounded-md ${
                viewMode === "field"
                  ? "bg-slate-700 shadow text-blue-400"
                  : "text-slate-500 hover:text-slate-300"
              }`}
              title="Field View"
            >
              <Map size={18} />
            </button>
            {getDisplayPosition && (
              <button
                onClick={() => setViewMode("tactical")}
                data-testid="btn-tactical-view"
                className={`p-1.5 rounded-md ${
                  viewMode === "tactical"
                    ? "bg-slate-700 shadow text-blue-400"
                    : "text-slate-500 hover:text-slate-300"
                }`}
                title="Tactical View"
              >
                <Crosshair size={18} />
              </button>
            )}
          </div>
        )}
      </div>


      {resolvedViewMode === "tactical" &&
      getDisplayPosition &&
      onPlayerDragEnd ? (
        <div className={`mb-6 ${selectionLocked ? "opacity-50" : ""}`}>
          <TacticalField
            homeTeamName={match.home_team.name}
            awayTeamName={match.away_team.name}
            homePlayers={match.home_team.players.filter((p) =>
              onFieldIds.home.has(p.id),
            )}
            awayPlayers={match.away_team.players.filter((p) =>
              onFieldIds.away.has(p.id),
            )}
            expelledPlayerIds={expelledPlayerIds}
            disciplinaryStatusByPlayer={disciplinaryStatusByPlayer}
            flipSides={flipSides}
            getDisplayPosition={getDisplayPosition}
            onPlayerDragEnd={onPlayerDragEnd}
            onPlayerClick={(player, anchor, location, side) => {
              if (selectionLocked) return;
              if (onFieldPlayerClick) {
                onFieldPlayerClick(player, anchor, location, side);
                return;
              }
              onPlayerClick(player);
            }}
            onDestinationClick={
              selectionLocked ? undefined : onFieldDestinationClick
            }
            overlay={resolvedFieldOverlay}
            showDestinationControls={showDestinationControls}
            draggingPlayerId={draggingPlayerId}
            onDragStart={onTacticalDragStart}
            onDragStop={onTacticalDragStop}
            allPositions={allTacticalPositions}
            dragLocked={effectiveDragLocked}
            visiblePlayerIds={visiblePlayerIds}
          />
        </div>
      ) : resolvedViewMode === "field" ? (
        <div className={`mb-6 ${selectionLocked ? "opacity-50" : ""}`}>
          <BasketballCourt
            homeTeamName={match.home_team.name}
            awayTeamName={match.away_team.name}
            homePlayers={match.home_team.players.filter((p) =>
              onFieldIds.home.has(p.id),
            )}
            awayPlayers={match.away_team.players.filter((p) =>
              onFieldIds.away.has(p.id),
            )}
            expelledPlayerIds={expelledPlayerIds}
            disciplinaryStatusByPlayer={disciplinaryStatusByPlayer}
            flipSides={flipSides}
            onPlayerClick={(player, anchor, location, side) => {
              if (selectionLocked) return;
              if (onFieldPlayerClick) {
                onFieldPlayerClick(player, anchor, location, side);
                return;
              }
              onPlayerClick(player);
            }}
            onDestinationClick={
              selectionLocked ? undefined : onFieldDestinationClick
            }
            overlay={resolvedFieldOverlay}
            showDestinationControls={showDestinationControls}
          />
        </div>
      ) : (
        <div
          className={`mb-6 ${
            selectedTeam === "both" ? "grid grid-cols-2 gap-4" : ""
          }`}
        >
          {teamsToShow.map(({ labelColor, team, tone }) => {
            const teamOnField =
              tone === "home" ? onFieldIds.home : onFieldIds.away;
            const onFieldPlayers = prioritizePlayers(
              team.players.filter((p) => teamOnField.has(p.id)),
            );
            const benchPlayers = prioritizePlayers(
              team.players.filter((p) => !teamOnField.has(p.id)),
            );
            const benchDisabled = selectionLocked;

            return (
              <div className="space-y-3" key={team.id}>
                <h3 className="font-medium text-slate-300 flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${labelColor}`}></span>
                  {team.name}
                </h3>
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    {t("onPitch", "On Pitch")}
                  </div>
                  {renderPlayerGrid(onFieldPlayers, tone, selectionLocked)}
                </div>

                {benchPlayers.length > 0 && (
                  <div
                    className="space-y-2 mt-4"
                    data-testid={`bench-section-${tone}`}
                  >
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                      {t("substitutes", "Substitutes")}
                    </div>
                    {renderPlayerGrid(benchPlayers, tone, benchDisabled)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerSelectorPanel;
